import { create } from 'zustand';
import { McpServer, RepoburgMcpServer, RepoburgMcpGateway, MarketplaceServer } from '@/types/api';
import {
  getMcpServers as apiGetMcpServers,
  getRepoburgMcpServer as apiGetRepoburgMcpServer,
  getRepoburgMcpGateway as apiGetRepoburgMcpGateway,
  createMcpConfig as apiCreateMcpConfig,
  updateMcpConfigStatus as apiUpdateMcpConfigStatus,
  deleteMcpConfig as apiDeleteMcpConfig,
  batchUpdateToolStatus as apiBatchUpdateToolStatus,
  startRepoburgMcpServer as apiStartRepoburgMcpServer,
  stopRepoburgMcpServer as apiStopRepoburgMcpServer,
  reconnectMcpServer as apiReconnectMcpServer,
} from '@/lib/api/mcp';
import {
  getMarketplaceServers as apiGetMarketplaceServers,
  initiateConnect as apiInitiateConnect,
  getConnectStatus as apiGetConnectStatus,
} from '@/lib/api/marketplace';
import { toast } from 'sonner';

interface McpState {
  mcpServers: McpServer[];
  repoburgServer: RepoburgMcpServer | null;
  repoburgGateway: RepoburgMcpGateway | null;
  isLoading: boolean;
  isCreating: boolean;
  isTogglingRepoburg: boolean;
  error: string | null;
  // Marketplace state
  marketplaceServers: MarketplaceServer[];
  marketplaceLoading: boolean;
  /** Set of marketplace IDs currently in OAuth flow */
  connectingIds: Set<string>;
  /** Set of marketplace IDs already connected */
  connectedIds: Set<string>;
  /** Set of server names whose tokens have expired and need re-authorization */
  expiredServerNames: Set<string>;
  fetchMcpServers: () => Promise<void>;
  fetchRepoburgServer: () => Promise<void>;
  fetchRepoburgGateway: () => Promise<void>;
  startRepoburgServer: () => Promise<void>;
  stopRepoburgServer: () => Promise<void>;
  createMcpConfig: (configJson: string) => Promise<boolean>;
  toggleMcpConfigStatus: (
    serverName: string,
    isActive: boolean,
  ) => Promise<void>;
  deleteMcpConfig: (serverName: string) => Promise<void>;
  /** toolKeys are `serverName__toolName` identifiers (double underscore). */
  batchUpdateToolStatus: (
    toolKeys: string[],
    isActive: boolean,
  ) => Promise<void>;
  // Marketplace actions
  fetchMarketplaceServers: () => Promise<void>;
  connectMarketplaceServer: (id: string) => Promise<void>;
  /** Called by WebSocket listener when marketplace connection succeeds */
  handleMarketplaceConnected: (marketplaceId: string, serverName: string) => void;
  /** Called by WebSocket listener when marketplace connection fails */
  handleMarketplaceError: (marketplaceId: string, error: string) => void;
  /** Re-authorize an existing remote MCP server with expired tokens */
  reconnectMcpServer: (serverName: string) => Promise<void>;
  /** Called by WebSocket listener when a token expires and re-auth is needed */
  handleTokenExpired: (serverName: string, marketplaceId: string | null) => void;
}

export const useMcpStore = create<McpState>((set, get) => ({
  mcpServers: [],
  repoburgServer: null,
  repoburgGateway: null,
  isLoading: false,
  isCreating: false,
  isTogglingRepoburg: false,
  error: null,
  // Marketplace state
  marketplaceServers: [],
  marketplaceLoading: false,
  connectingIds: new Set(),
  connectedIds: new Set(),
  expiredServerNames: new Set(),

  fetchMcpServers: async () => {
    set({ isLoading: true, error: null });
    try {
      const servers = await apiGetMcpServers();
      set({ mcpServers: servers, isLoading: false });
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : 'Failed to fetch MCP servers.';
      set({ error: errorMsg, isLoading: false });
      toast.error(errorMsg);
    }
  },

  fetchRepoburgServer: async () => {
    try {
      const server = await apiGetRepoburgMcpServer();
      set({ repoburgServer: server });
    } catch (err) {
      const errorMsg =
        err instanceof Error
          ? err.message
          : 'Failed to fetch Repoburg MCP server info.';
      toast.error(errorMsg);
    }
  },

  fetchRepoburgGateway: async () => {
    try {
      const gateway = await apiGetRepoburgMcpGateway();
      set({ repoburgGateway: gateway });
    } catch (err) {
      const errorMsg =
        err instanceof Error
          ? err.message
          : 'Failed to fetch Repoburg MCP gateway info.';
      toast.error(errorMsg);
    }
  },

  startRepoburgServer: async () => {
    set({ isTogglingRepoburg: true });
    try {
      await apiStartRepoburgMcpServer();
      toast.success('Repoburg MCP server started.');
      await get().fetchRepoburgServer();
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : 'Failed to start Repoburg MCP server.';
      toast.error(errorMsg);
    } finally {
      set({ isTogglingRepoburg: false });
    }
  },

  stopRepoburgServer: async () => {
    set({ isTogglingRepoburg: true });
    try {
      await apiStopRepoburgMcpServer();
      toast.success('Repoburg MCP server stopped.');
      await get().fetchRepoburgServer();
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : 'Failed to stop Repoburg MCP server.';
      toast.error(errorMsg);
    } finally {
      set({ isTogglingRepoburg: false });
    }
  },

  createMcpConfig: async (configJson: string) => {
    set({ isCreating: true, error: null });
    try {
      await apiCreateMcpConfig(configJson);
      toast.success('MCP server configuration saved and connected.');
      await get().fetchMcpServers();
      set({ isCreating: false });
      return true;
    } catch (err) {
      const errorMsg =
        err instanceof Error
          ? err.message
          : 'Failed to create MCP configuration.';
      set({ error: errorMsg, isCreating: false });
      toast.error(errorMsg);
      return false;
    }
  },

  toggleMcpConfigStatus: async (serverName: string, isActive: boolean) => {
    // This action can now be simplified as the tool list is part of the server object
    const originalServers = get().mcpServers;
    toast.info(
      `${isActive ? 'Activating' : 'Deactivating'} ${serverName}...`,
    );
    try {
      await apiUpdateMcpConfigStatus(serverName, isActive);
      toast.success(
        `Server ${serverName} ${isActive ? 'activated' : 'deactivated'}.`,
      );
      await get().fetchMcpServers(); // Refresh to get updated tool list
    } catch (err) {
      const errorMsg =
        err instanceof Error
          ? err.message
          : `Failed to update ${serverName}.`;
      toast.error(errorMsg);
      set({ mcpServers: originalServers });
    }
  },

  deleteMcpConfig: async (serverName: string) => {
    const originalServers = get().mcpServers;
    set((state) => ({
      mcpServers: state.mcpServers.filter(
        (s) => s.server_name !== serverName,
      ),
    }));
    toast.info(`Deleting ${serverName}...`);
    try {
      await apiDeleteMcpConfig(serverName);
      toast.success(`Server ${serverName} deleted.`);
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : `Failed to delete ${serverName}.`;
      toast.error(errorMsg);
      set({ mcpServers: originalServers });
    }
  },

  batchUpdateToolStatus: async (toolKeys: string[], isActive: boolean) => {
    const originalServers = get().mcpServers;
    // Optimistic update — match tools by their serverName__toolName key
    set((state) => ({
      mcpServers: state.mcpServers.map((server) => ({
        ...server,
        tools: server.tools.map((tool) =>
          toolKeys.includes(`${server.server_name}__${tool.tool_name}`)
            ? { ...tool, is_active: isActive }
            : tool,
        ),
      })),
    }));

    try {
      await apiBatchUpdateToolStatus(toolKeys, isActive);
      toast.success(
        `${toolKeys.length} tool(s) ${
          isActive ? 'activated' : 'deactivated'
        }.`,
      );
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : `Failed to update tools.`;
      toast.error(errorMsg);
      set({ mcpServers: originalServers }); // Revert on failure
    }
  },

  // ─── Marketplace Actions ────────────────────────────────────────────

  fetchMarketplaceServers: async () => {
    set({ marketplaceLoading: true });
    try {
      const servers = await apiGetMarketplaceServers();
      // Determine which marketplace IDs are already connected
      const connectedIds = new Set(
        get()
          .mcpServers.filter((s) => s.marketplace_id)
          .map((s) => s.marketplace_id!),
      );
      set({ marketplaceServers: servers, marketplaceLoading: false, connectedIds });
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : 'Failed to fetch marketplace servers.';
      set({ marketplaceLoading: false });
      toast.error(errorMsg);
    }
  },

  connectMarketplaceServer: async (id: string) => {
    // Add to connecting set
    set((state) => ({
      connectingIds: new Set(state.connectingIds).add(id),
    }));

    try {
      const { authUrl } = await apiInitiateConnect(id);

      if (authUrl) {
        // Open the authorization URL in a new browser tab
        window.open(authUrl, '_blank');
        toast.info('Authorization page opened in a new tab. Please authorize access.');
      } else {
        // Already had tokens — connection completed immediately
        toast.success('Server connected successfully.');
        set((state) => {
          const connectingIds = new Set(state.connectingIds);
          connectingIds.delete(id);
          const connectedIds = new Set(state.connectedIds);
          connectedIds.add(id);
          return { connectingIds, connectedIds };
        });
        await get().fetchMcpServers();
        return;
      }

      // Poll for connection status every 2 seconds
      const maxPolls = 150; // 5 minutes timeout
      let polls = 0;
      const poll = async (): Promise<void> => {
        if (polls >= maxPolls) {
          set((state) => {
            const connectingIds = new Set(state.connectingIds);
            connectingIds.delete(id);
            return { connectingIds };
          });
          toast.error('Connection timed out. Please try again.');
          return;
        }
        polls++;

        try {
          const status = await apiGetConnectStatus(id);
          if (status.status === 'connected') {
            set((state) => {
              const connectingIds = new Set(state.connectingIds);
              connectingIds.delete(id);
              const connectedIds = new Set(state.connectedIds);
              connectedIds.add(id);
              return { connectingIds, connectedIds };
            });
            toast.success('MCP server connected successfully!');
            await get().fetchMcpServers();
            return;
          }
          if (status.status === 'error') {
            set((state) => {
              const connectingIds = new Set(state.connectingIds);
              connectingIds.delete(id);
              return { connectingIds };
            });
            toast.error(status.error || 'Connection failed.');
            return;
          }
          // Still pending — continue polling
          setTimeout(poll, 2000);
        } catch {
          // Network error — continue polling
          setTimeout(poll, 2000);
        }
      };

      // Start polling after a short delay to allow browser to open
      setTimeout(poll, 3000);
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : 'Failed to initiate connection.';
      set((state) => {
        const connectingIds = new Set(state.connectingIds);
        connectingIds.delete(id);
        return { connectingIds };
      });
      toast.error(errorMsg);
    }
  },

  handleMarketplaceConnected: (marketplaceId: string, serverName: string) => {
    set((state) => {
      const connectingIds = new Set(state.connectingIds);
      connectingIds.delete(marketplaceId);
      const connectedIds = new Set(state.connectedIds);
      connectedIds.add(marketplaceId);
      return { connectingIds, connectedIds };
    });
    toast.success(`Connected to ${serverName}!`);
    get().fetchMcpServers();
  },

  handleMarketplaceError: (marketplaceId: string, error: string) => {
    set((state) => {
      const connectingIds = new Set(state.connectingIds);
      connectingIds.delete(marketplaceId);
      return { connectingIds };
    });
    toast.error(`Connection failed: ${error}`);
  },

  reconnectMcpServer: async (serverName: string) => {
    try {
      const { authUrl } = await apiReconnectMcpServer(serverName);

      if (authUrl) {
        window.open(authUrl, '_blank');
        toast.info('Authorization page opened in a new tab. Please authorize access.');

        // Poll for the marketplace connect status using the marketplace_id
        // from the server list (the old config was deleted, new one is pending)
        const maxPolls = 150;
        let polls = 0;
        const poll = async (): Promise<void> => {
          if (polls >= maxPolls) {
            toast.error('Re-authorization timed out. Please try again.');
            return;
          }
          polls++;
          // Wait a bit, then refresh the server list to see if the new config appeared
          await new Promise((resolve) => setTimeout(resolve, 2000));
          try {
            await get().fetchMcpServers();
            const servers = get().mcpServers;
            // Check if the server is back and has valid token status
            const server = servers.find((s) => s.server_name === serverName);
            if (server && server.is_active && server.token_status !== 'expired') {
              set((state) => {
                const expiredServerNames = new Set(state.expiredServerNames);
                expiredServerNames.delete(serverName);
                return { expiredServerNames };
              });
              toast.success(`Re-authorized ${serverName} successfully!`);
              return;
            }
            // Still pending — continue polling
            setTimeout(poll, 0);
          } catch {
            setTimeout(poll, 0);
          }
        };
        setTimeout(poll, 3000);
      } else {
        // No auth URL needed — connection completed immediately
        set((state) => {
          const expiredServerNames = new Set(state.expiredServerNames);
          expiredServerNames.delete(serverName);
          return { expiredServerNames };
        });
        toast.success(`Re-authorized ${serverName} successfully!`);
        await get().fetchMcpServers();
      }
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : 'Failed to re-authorize MCP server.';
      toast.error(errorMsg);
    }
  },

  handleTokenExpired: (serverName: string, _marketplaceId: string | null) => {
    set((state) => {
      const expiredServerNames = new Set(state.expiredServerNames);
      expiredServerNames.add(serverName);
      return { expiredServerNames };
    });
    toast.warning(
      `MCP server "${serverName}" needs re-authorization. Click "Re-authorize" in MCP settings.`,
      { duration: 8000 },
    );
    get().fetchMcpServers();
  },
}));